// /src/CollabSpace.tsx

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Avatar,
  InputAdornment,
  Grid,
  Card,
  CardContent,
  Alert,
  SelectChangeEvent,
} from '@mui/material';
import {
  Add as AddIcon,
  People as PeopleIcon,
  Search as SearchIcon,
  LightbulbOutlined as LightbulbOutlinedIcon,
  ArrowBack as ArrowBackIcon, // Import the back arrow icon
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';

// Define the KnowledgeItem interface
export interface KnowledgeItem {
  id: string;
  title: string;
  type: string;
  tags: string[];
  content: string;
  createdBy: string;
  createdAt: string; // ISO string date
}

interface CollabSpaceProps {
  knowledgeItems?: KnowledgeItem[];
  onAddKnowledgeItem: (newItem: Omit<KnowledgeItem, 'id' | 'createdAt' | 'createdBy'>) => Promise<{ success: boolean; error?: string }>;
  onFetchKnowledgeItems: (type?: string, search?: string) => void;
  userName: string;
  setUserName: (name: string) => void;
  onGoBack: () => void; // Add a prop for the go back action
}

const KNOWLEDGE_TYPES = [
  'Common Issue & Solution',
  'How-to Guides',
  'Learning Resources',
  'Best Practices',
  'Troubleshooting',
  'FAQs',
];

const QUICK_ADD_SUGGESTIONS = {
  'Common Issues': [
    'Authentication timeout problems',
    'Build failures and solutions',
    'Database connection issues',
    'API rate limiting errors',
  ],
  'How-to Guides': [
    'Setting up development environment',
    'Code review best practices',
    'Deployment procedures',
    'Testing strategies',
  ],
  'Learning Resources': [
    'Framework documentation links',
    'Useful libraries and tools',
    'Performance optimization tips',
    'Security best practices',
  ],
};

const CollabSpace: React.FC<CollabSpaceProps> = ({
  knowledgeItems = [],
  onAddKnowledgeItem,
  onFetchKnowledgeItems,
  userName,
  setUserName,
  onGoBack, // Destructure the new prop
}) => {
  const [openAddModal, setOpenAddModal] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newType, setNewType] = useState(KNOWLEDGE_TYPES[0]);
  const [newTags, setNewTags] = useState('');
  const [newContent, setNewContent] = useState('');
  const [filterType, setFilterType] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [addMessage, setAddMessage] = useState('');

  useEffect(() => {
    if (typeof onFetchKnowledgeItems === 'function') {
      onFetchKnowledgeItems(filterType, searchTerm);
    }
  }, [filterType, searchTerm, onFetchKnowledgeItems]);

  const handleOpenAddModal = () => {
    setOpenAddModal(true);
    setNewTitle('');
    setNewType(KNOWLEDGE_TYPES[0]);
    setNewTags('');
    setNewContent('');
    setAddMessage('');
  };

  const handleCloseAddModal = () => {
    setOpenAddModal(false);
  };

  const handleAddItem = async () => {
    if (!newTitle.trim() || !newContent.trim()) {
      setAddMessage('Title and Content cannot be empty.');
      return;
    }

    const tagsArray = newTags.split(',').map(tag => tag.trim()).filter(tag => tag !== '');

    const result = await onAddKnowledgeItem({
      title: newTitle.trim(),
      type: newType,
      tags: tagsArray,
      content: newContent.trim(),
    });

    if (result.success) {
      setAddMessage('Knowledge item added successfully!');
      handleCloseAddModal();
    } else {
      setAddMessage(result.error || 'Failed to add knowledge item.');
    }
  };

  const handleFilterChange = (event: SelectChangeEvent) => {
    setFilterType(event.target.value);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleUserNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUserName(event.target.value);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* --- ADDED GO BACK BUTTON --- */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0 }}>
          <PeopleIcon /> Team Collaboration Space
        </Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={onGoBack}
        >
          Go Back
        </Button>
      </Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Share knowledge, document solutions, and build team expertise
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PeopleIcon /> Knowledge Base
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAddModal}
          >
            Add Knowledge Item
          </Button>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <FormControl variant="outlined" sx={{ minWidth: 200 }}>
            <InputLabel>Filter by Type</InputLabel>
            <Select
              value={filterType}
              onChange={handleFilterChange}
              label="Filter by Type"
            >
              <MenuItem value="">All Types</MenuItem>
              {KNOWLEDGE_TYPES.map((type) => (
                <MenuItem key={type} value={type}>{type}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            variant="outlined"
            placeholder="Search knowledge base..."
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{ flexGrow: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {knowledgeItems.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 5 }}>
            <Avatar sx={{ width: 80, height: 80, mx: 'auto', mb: 3, bgcolor: 'primary.light' }}>
              <PeopleIcon fontSize="large" />
            </Avatar>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No matching items found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try adjusting your search or filter criteria.
            </Typography>
          </Box>
        ) : (
          <List>
            {knowledgeItems.map((item) => (
              <Paper key={item.id} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                <ListItem disablePadding sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                  <ListItemText
                    primary={
                      <Typography variant="h6" sx={{ mb: 0.5 }}>{item.title}</Typography>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Type: <Chip label={item.type} size="small" variant="outlined" sx={{ mr: 1 }} />
                          Tags: {item.tags.map(tag => <Chip key={tag} label={tag} size="small" sx={{ mr: 0.5 }} />)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          Created by: {item.createdBy} on {new Date(item.createdAt).toLocaleString()}
                        </Typography>
                        <ReactMarkdown>{item.content}</ReactMarkdown>
                      </Box>
                    }
                  />
                </ListItem>
              </Paper>
            ))}
          </List>
        )}
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LightbulbOutlinedIcon /> Quick Add Suggestions
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Here are some ideas for knowledge items you can add:
        </Typography>
        <Grid container spacing={2}>
          {Object.entries(QUICK_ADD_SUGGESTIONS).map(([category, suggestions]) => (
            <Grid item xs={12} sm={4} key={category}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>{category}</Typography>
                  <List dense disablePadding>
                    {suggestions.map((suggestion, index) => (
                      <ListItem key={index} disablePadding>
                        <ListItemText primary={`• ${suggestion}`} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Dialog open={openAddModal} onClose={handleCloseAddModal} fullWidth maxWidth="sm">
        <DialogTitle>Add New Knowledge Item</DialogTitle>
        <DialogContent dividers>
          {addMessage && (
            <Alert severity={addMessage.includes('successfully') ? 'success' : 'error'} sx={{ mb: 2 }}>
              {addMessage}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            id="title"
            label="Title"
            type="text"
            fullWidth
            variant="outlined"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth margin="dense" variant="outlined" sx={{ mb: 2 }}>
            <InputLabel id="type-label">Type</InputLabel>
            <Select
              labelId="type-label"
              id="type"
              value={newType}
              label="Type"
              onChange={(e) => setNewType(e.target.value as string)}
            >
              {KNOWLEDGE_TYPES.map((type) => (
                <MenuItem key={type} value={type}>{type}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            id="tags"
            label="Tags (comma-separated)"
            type="text"
            fullWidth
            variant="outlined"
            value={newTags}
            onChange={(e) => setNewTags(e.target.value)}
            helperText="Add relevant tags to help others find this item"
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            id="content"
            label="Content"
            type="text"
            fullWidth
            multiline
            rows={6}
            variant="outlined"
            value={newContent}
            onChange={(e) => setNewContent(e.target.value)}
            helperText="Use markdown formatting for better readability"
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            id="userName"
            label="Created By (Your Name)"
            type="text"
            fullWidth
            variant="outlined"
            value={userName}
            onChange={handleUserNameChange}
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddModal} color="primary">Cancel</Button>
          <Button onClick={handleAddItem} variant="contained" color="primary">Add Item</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CollabSpace;